import React from 'react';
import PropTypes from 'prop-types';
import ToolIcon, { vendorOfTool } from './ToolIcon';
import './VendorName.css';

/** Flow's own mark: the app's logo (public/flow.svg, the same asset as the sidebar's), under the app's base path. */
const FLOW_MARK_URL = `${import.meta.env.BASE_URL || '/'}flow.svg`;

/**
 * The vendor behind a name that cites an AI tool, agent or model provider ("Claude Code", "Mistral", "Flow chat",
 * "Claude Code for you", "Anthropic"): `{ flow: true }` for Flow itself (Flow, Flow chat, Flow agent), else
 * vendorOfTool's `{ icon, label }`, else null.
 */
export function vendorMarkOf(name) {
  const text = String(name || '').trim();
  if (!text) { return null; }
  if (/^flow\b/i.test(text)) { return { flow: true, icon: null, label: 'Flow' }; }
  return vendorOfTool(text);
}

/** Just the vendor's mark (small, aria-hidden), or nothing for an unknown vendor. */
export function VendorMark({ name, size = 14 }) {
  const vendor = vendorMarkOf(name);
  if (!vendor) { return null; }
  if (vendor.flow) {
    return <img alt="" aria-hidden="true" className="tool-icon vendor-mark-flow" height={size} src={FLOW_MARK_URL} width={size} />;
  }
  if (!vendor.icon) {
    // A vendor without a mark (OpenAI) gets ToolIcon's lettered tile, its letter drawn by CSS so the text stays the name.
    return (
      <span aria-hidden="true" className="tool-icon tool-icon-tile vendor-mark-tile" data-letter={vendor.label.charAt(0).toUpperCase()} style={{ width: size, height: size, fontSize: Math.round(size * 0.6) }} />
    );
  }
  return <ToolIcon icon={vendor.icon} label={vendor.label} size={size} />;
}

VendorMark.propTypes = {
  name: PropTypes.string,
  size: PropTypes.number,
};

/**
 * A vendor's name as text with its mark before it, the same everywhere an AI vendor or tool is cited (Projects'
 * Made with, AI tool sessions, AI used in this project, agents at work…). `name` picks the vendor; the text shown is
 * `children` when given (e.g. "Claude Code for you"), else `name`. An unknown vendor renders the text alone.
 */
export default function VendorName({ name, size = 14, children = undefined }) {
  const text = children === undefined ? name : children;
  if (!vendorMarkOf(name)) { return <>{text}</>; }
  return (
    <span className="vendor-name" data-vendor={vendorMarkOf(name).label}>
      <span className="vendor-name-mark"><VendorMark name={name} size={size} /></span>
      {text}
    </span>
  );
}

VendorName.propTypes = {
  children: PropTypes.node,
  name: PropTypes.string,
  size: PropTypes.number,
};
